import React, { useState } from 'react';
import { THEME_A } from '../themeAConfig';
import Flashcards from './Flashcards';
import Quiz from './Quiz';
import SqlExercise from './SqlExercise';
import '../themeA.css';

/**
 * Theme A (theory) study hub.
 *
 * Owns the topic/subtopic picker and the SL/HL toggle, and renders the
 * Flashcards / Quiz screens for the chosen scope. This is the focused Theme A
 * study entry point (Phase 2 core); the unified cross-theme dashboard with the
 * shared avatar lands separately.
 *
 * Props:
 *   activeLevel, onLevelChange  — shared SL/HL track (lifted to App).
 *   onBack                      — leave Theme A (return to the main dashboard).
 */

const LEVELS = [
  { id: 'sl', label: 'SL', icon: '🟢' },
  { id: 'hl', label: 'HL', icon: '⚡' },
];

function LevelToggle({ activeLevel, onLevelChange }) {
  return (
    <div className="cd-lang-toggle" role="tablist" aria-label="IB level track">
      {LEVELS.map(lvl => (
        <button
          key={lvl.id}
          role="tab"
          aria-selected={activeLevel === lvl.id}
          className={`cd-lang-btn${activeLevel === lvl.id ? ' cd-lang-btn-active' : ''}`}
          onClick={() => onLevelChange(lvl.id)}
        >
          <span className="cd-lang-icon" aria-hidden="true">{lvl.icon}</span>
          {lvl.label}
        </button>
      ))}
    </div>
  );
}

export default function ThemeAHub({ activeLevel = 'hl', onLevelChange, onBack }) {
  // view: { mode: 'flashcards' | 'quiz', scope, title } or null for the menu.
  const [view, setView] = useState(null);

  const open = (mode, scope, title) => setView({ mode, scope, title });
  const backToMenu = () => setView(null);

  if (view) {
    const Screen = view.mode === 'sql' ? SqlExercise
                 : view.mode === 'quiz' ? Quiz
                 : Flashcards;
    return (
      <div className="ta-page">
        <Screen
          scope={view.scope}
          level={activeLevel}
          scopeTitle={view.title}
          onBack={backToMenu}
        />
      </div>
    );
  }

  return (
    <div className="ta-page">
      <div className="ta-canvas">
        <div className="ta-header">
          <button className="ta-back" onClick={onBack}>‹ Dashboard</button>
          <div>
            <h1 className="ta-title">Theme A — Theory</h1>
            <p className="ta-subtitle">Flashcards &amp; quizzes across computer systems, networks, databases &amp; ML</p>
          </div>
          <div className="ta-header-spacer" />
          <LevelToggle activeLevel={activeLevel} onLevelChange={onLevelChange} />
        </div>

        {THEME_A.map(topic => (
          <section className="ta-topic" key={topic.id}>
            <div className="ta-topic-head">
              <span className="ta-topic-icon" aria-hidden="true">{topic.icon}</span>
              <h2 className="ta-topic-title">{topic.title}</h2>
            </div>

            {topic.subtopics.map(sub => {
              const muted = activeLevel === 'sl' && sub.hlOnly;
              return (
                <div className={`ta-sub${muted ? ' ta-muted' : ''}`} key={sub.scope}>
                  <span className="ta-sub-title">
                    {sub.title}
                    {sub.hlOnly && <span className="ta-hl-badge">HL only</span>}
                  </span>
                  <div className="ta-sub-actions">
                    <button
                      className="ta-btn ta-btn-secondary"
                      disabled={muted}
                      onClick={() => open('flashcards', sub.scope, sub.title)}
                    >
                      Flashcards
                    </button>
                    <button
                      className="ta-btn ta-btn-primary"
                      disabled={muted}
                      onClick={() => open('quiz', sub.scope, sub.title)}
                    >
                      Quiz
                    </button>
                    {sub.scope === 'A3.3' && (
                      <button
                        className="ta-btn ta-btn-primary"
                        disabled={muted}
                        onClick={() => open('sql', sub.scope, sub.title)}
                      >
                        SQL
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        ))}
      </div>
    </div>
  );
}
